// PKCE (Proof Key for Code Exchange) helpers for Deriv OAuth.
// Used for the new auth.deriv.com flow that newly-registered apps require.
//
// v8.9.127 — switched from sessionStorage to localStorage. The Deriv
// OAuth flow navigates the user away to auth.deriv.com and back, which
// in some browsers (Safari / strict cookie policies / Chrome with
// 3rd-party isolation) treats the round-trip as a fresh session,
// wiping sessionStorage and breaking the code → token exchange.
// localStorage survives cross-site redirects in every modern browser.

const STORAGE_KEY = "vts_pkce_v1";

const base64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const randomString = (len = 64) => {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return base64url(arr).slice(0, len);
};

export const generatePkce = async () => {
  const verifier = randomString(64);
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const challenge = base64url(digest);
  const state = randomString(24);
  const stored = { verifier, state, created_at: Date.now() };
  // Write to BOTH storages so a follow-up read works even if one of them
  // is wiped (e.g., session-restoring browsers nuke sessionStorage on
  // cross-site nav). Belt-and-suspenders.
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(stored)); } catch (_e) { /* quota */ }
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored)); } catch (_e) { /* quota */ }
  return { verifier, challenge, state };
};

export const consumePkce = () => {
  // Try localStorage first (the resilient one), fall back to sessionStorage
  // for users mid-upgrade from older builds.
  let raw = null;
  try { raw = localStorage.getItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
  if (!raw) {
    try { raw = sessionStorage.getItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
  }
  if (!raw) return null;
  try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
